import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoData from '@/components/Formulario/CampoData';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoDinheiro from '@/components/Formulario/CampoDinheiro';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import Grid, { GridItem } from '../../components/layout/Grid';
import AlertBox from '../../components/layout/AlertBox';
import { useQuery } from 'react-query';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const tipoOptions = [
  { id: 'receita', label: 'Receita' },
  { id: 'despesa', label: 'Despesa' },
  { id: 'saldo', label: 'Saldo (ajuste)' },
];

export const FormLancamento = ({
  value,
  onChange,
  fieldErrors,
}: {
  value: any;
  onChange: any;
  fieldErrors: any;
}) => {
  const tipo = value?.tipo ?? 'despesa';
  const isSaldo = tipo === 'saldo';

  const qCategorias = useQuery(
    ['categoria_financeira/box', tipo],
    async () => {
      const res = await axios.get('/api/categoria_financeira/box', {
        params: { tipo },
      });
      return res.data;
    },
    { enabled: !isSaldo },
  );
  const categoriasOptions =
    qCategorias.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const qCentrosCusto = useQuery('centro_custo/box', async () => {
    const res = await axios.get('/api/centro_custo/box');
    return res.data;
  });
  const centrosCustoOptions =
    qCentrosCusto.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const qSaldoMaisAntigo = useQuery(
    'lancamento_financeiro/saldo_mais_antigo',
    async () => {
      const res = await axios.get('/api/lancamento_financeiro/saldo_mais_antigo');
      return res.data;
    },
    { enabled: !isSaldo },
  );
  const saldoMaisAntigo: string | null = qSaldoMaisAntigo.data?.data ?? null;

  const mostrarAvisoAntesDoSaldo =
    !isSaldo &&
    !!saldoMaisAntigo &&
    !!value?.data_vencimento &&
    value.data_vencimento < saldoMaisAntigo;

  return (
    <Grid>
      <GridItem sm={12} md={4}>
        <CampoSeletor
          noSearch
          instanceId="tipo"
          value={tipo}
          onChange={v => onChange({ ...value, tipo: v, id_categoria: null })}
          label="Tipo"
          options={tipoOptions}
          error={fieldErrors?.tipo}
        />
      </GridItem>
      <GridItem sm={12} md={8}>
        <CampoTexto
          instanceId="descricao"
          value={value?.descricao ?? ''}
          onChange={v => onChange({ ...value, descricao: v })}
          label={isSaldo ? 'Descrição (opcional)' : 'Descrição'}
          placeholder={isSaldo ? 'Saldo' : undefined}
          error={fieldErrors?.descricao}
        />
      </GridItem>
      <GridItem sm={12} md={6}>
        <CampoDinheiro
          instanceId="valor"
          value={value?.valor ?? 0}
          onChange={v => onChange({ ...value, valor: v })}
          label={isSaldo ? 'Saldo em caixa nesta data' : 'Valor'}
          permitirNegativo={isSaldo}
          error={fieldErrors?.valor}
        />
      </GridItem>
      <GridItem sm={12} md={6}>
        <CampoData
          instanceId="data_vencimento"
          type="date"
          value={value?.data_vencimento ?? ''}
          onChange={v => onChange({ ...value, data_vencimento: v })}
          label={isSaldo ? 'Data do saldo' : 'Vencimento'}
          error={fieldErrors?.data_vencimento}
        />
      </GridItem>
      {mostrarAvisoAntesDoSaldo && (
        <GridItem sm={12}>
          <AlertBox variant="warning">
            Este lançamento é anterior ao saldo mais antigo cadastrado (
            {format(parseISO(saldoMaisAntigo!), 'dd/MM/yyyy')}) e não vai
            aparecer no Fluxo de Caixa.
          </AlertBox>
        </GridItem>
      )}
      {!isSaldo && (
        <GridItem sm={12} md={6}>
          <CampoData
            instanceId="data_pagamento"
            type="date"
            value={value?.data_pagamento ?? ''}
            onChange={v => onChange({ ...value, data_pagamento: v || null })}
            label="Pagamento (deixe em branco se ainda não pago)"
            error={fieldErrors?.data_pagamento}
          />
        </GridItem>
      )}
      {!isSaldo && (
        <GridItem sm={12} md={6}>
          <CampoSeletor
            nullable
            instanceId="id_categoria"
            value={value?.id_categoria ?? null}
            onChange={v => onChange({ ...value, id_categoria: v })}
            label="Categoria"
            placeholder="(Sem categoria)"
            error={fieldErrors?.id_categoria}
            options={categoriasOptions}
          />
        </GridItem>
      )}
      {!isSaldo && (
        <GridItem sm={12} md={6}>
          <CampoSeletor
            nullable
            instanceId="id_centro_custo"
            value={value?.id_centro_custo ?? null}
            onChange={v => onChange({ ...value, id_centro_custo: v })}
            label="Centro de custo"
            placeholder="(Sem centro de custo)"
            error={fieldErrors?.id_centro_custo}
            options={centrosCustoOptions}
          />
        </GridItem>
      )}
      <GridItem sm={12}>
        <CampoMemo
          instanceId="obs"
          value={value?.obs ?? ''}
          onChange={v => onChange({ ...value, obs: v })}
          label="Observações"
          error={fieldErrors?.obs}
        />
      </GridItem>
    </Grid>
  );
};
